import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { useT } from "@/i18n/LanguageContext";

const serif = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

export default function JoinTheJourney() {
  const { login, register } = useAuth();
  const { t } = useT();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("mode") === "login" ? "login" : "register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [foundersDisplay, setFoundersDisplay] = useState("yes");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "register") await register(name, email, password, foundersDisplay);
      else await login(email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full bg-black/40 border border-white/10 focus:border-gold/60 outline-none px-4 py-3.5 text-bone text-sm placeholder:text-bone/30 transition-colors";

  return (
    <main data-testid="join-the-journey" className="min-h-screen bg-[#050505] pt-32 pb-24 px-6">
      <div className="max-w-md mx-auto">
        <p className="text-[11px] tracking-[0.28em] uppercase text-gold text-center mb-5">
          {t("join.eyebrow")}
        </p>
        <h1 style={serif} className="text-4xl sm:text-5xl text-bone text-center font-medium tracking-tight mb-4">
          {mode === "register" ? t("join.title") : t("join.loginTitle")}
        </h1>
        <p className="text-sm text-bone/55 text-center leading-relaxed mb-3">{t("join.support")}</p>
        {mode === "register" && (
          <p className="text-sm text-bone/40 text-center leading-relaxed mb-10">{t("join.welcomeNote")}</p>
        )}
        {mode === "login" && <div className="mb-10" />}

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <input
              data-testid="join-name-input"
              className={inputCls}
              placeholder={t("join.name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={80}
            />
          )}
          <input
            data-testid="join-email-input"
            className={inputCls}
            type="email"
            placeholder={t("join.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            data-testid="join-password-input"
            className={inputCls}
            type="password"
            placeholder={t("join.password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />

          {mode === "register" && (
            <div className="border border-gold/20 bg-black/30 p-4 space-y-3" data-testid="join-founders-block">
              <p className="text-[10px] tracking-[0.26em] uppercase text-gold">{t("join.fw.title")}</p>
              <p className="text-xs text-bone/55 leading-relaxed">{t("join.fw.q")}</p>
              <label data-testid="join-founders-yes" className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="founders-display"
                  checked={foundersDisplay === "yes"}
                  onChange={() => setFoundersDisplay("yes")}
                  className="mt-1 accent-[#C5A059]"
                />
                <span className="text-sm text-bone/80 leading-relaxed">{t("join.fw.yes")}</span>
              </label>
              <label data-testid="join-founders-no" className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="founders-display"
                  checked={foundersDisplay === "no"}
                  onChange={() => setFoundersDisplay("no")}
                  className="mt-1 accent-[#C5A059]"
                />
                <span className="text-sm text-bone/80 leading-relaxed">{t("join.fw.no")}</span>
              </label>
            </div>
          )}

          {error && (
            <p data-testid="join-error" className="text-sm text-red-400/90 leading-snug">{error}</p>
          )}
          {mode === "login" && (
            <Link to="/reset-password" data-testid="join-forgot-password"
              className="block text-right text-[11px] tracking-[0.14em] uppercase text-bone/40 hover:text-gold transition-colors">
              {t("join.forgotPassword")}
            </Link>
          )}
          <button
            data-testid="join-submit-button"
            type="submit"
            disabled={busy}
            className="w-full text-[12px] tracking-[0.22em] uppercase text-ink bg-gold hover:bg-gold-hover disabled:opacity-60 px-5 py-4 transition-colors"
          >
            {busy ? "…" : mode === "register" ? t("join.registerCta") : t("join.loginCta")}
          </button>
        </form>

        <button
          data-testid="join-switch-mode"
          type="button"
          onClick={() => { setMode(mode === "register" ? "login" : "register"); setError(""); }}
          className="mt-8 w-full text-center text-[12px] tracking-[0.14em] uppercase text-bone/50 hover:text-gold transition-colors"
        >
          {mode === "register" ? t("join.haveAccount") : t("join.newHere")}
        </button>

        <p style={serif} className="mt-16 text-center text-lg text-bone/40 italic leading-relaxed">
          “{t("join.innerFreedom")}”
        </p>
      </div>
    </main>
  );
}
